import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { ReceiveConvoyNotDispatchedJourneyDto } from '@/entities/receive';

interface ReceiveConvoyNotDispatchedProps {
  journeys?: ReceiveConvoyNotDispatchedJourneyDto[] | null;
}

/**
 * A partly dispatched convoy is received for the trucks that have left; this
 * names the ones that have not, so the operator does not look for them in the
 * table. They stay awaiting dispatch and are received once they arrive.
 */
export function ReceiveConvoyNotDispatched({ journeys }: ReceiveConvoyNotDispatchedProps) {
  const { t } = useTranslation();

  if (!journeys?.length) return null;

  return (
    <Alert variant="warning">
      <AlertTriangle />
      <AlertTitle>
        {t('receive.convoy.notDispatched.title', 'Part of this convoy has not been dispatched yet')}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-1">
        <span>
          {t(
            'receive.convoy.notDispatched.desc',
            'These trucks are not part of this receipt. They can be received once they are dispatched:'
          )}
        </span>
        <ul className="list-disc pl-5">
          {journeys.map((journey) => (
            <li key={journey.journeyId}>
              {journey.vehicleIdentifier || '—'}
              {journey.waybills?.length ? (
                <span className="ml-2 font-mono text-muted-foreground">{journey.waybills.join(', ')}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
